import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DatePopover } from "@/components/date-popover"
import { LockedBanner } from "@/features/transit/components/locked-banner"
import { SectionTitle } from "@/features/transit/components/section-title"
import { StageDocGrid } from "@/features/transit/components/stage-doc-grid"
import { usePatchOT } from "@/features/transit/hooks/use-transit-mutations"
import { isOTLocked } from "@/features/transit/lib/ot-math"
import { paths } from "@/routes/paths"
import type { OT } from "@/features/transit/api/types"

interface CreatedPanelProps {
  ot: OT
}

// Created panel:
//   - shipping/booking header (freightForwarder, vessel, voyage, ETD,
//     POL/POD, shipmentSize) — patches the OT row
//   - read-only contract summary
//   - attached lots list with detach (only at this stage)
//   - 3 slot docs via StageDocGrid
//
// We commit on blur for text inputs to avoid hammering the backend on
// every keystroke. The PageHeader-level state is already wired through
// `usePatchOT`'s response, so the cache stays consistent.
export function CreatedPanel({ ot }: CreatedPanelProps) {
  const { t } = useTranslation("transit")
  const patch = usePatchOT(ot.id)
  const locked = isOTLocked(ot)

  return (
    <div className="flex flex-col gap-4">
      {locked ? <LockedBanner ot={ot} /> : null}
      <Card className="flex flex-col gap-5 p-5">
        <SectionTitle
          eyebrow={t("created.eyebrow")}
          title={t("created.title")}
          description={t("created.description")}
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field
            label={t("created.freightForwarder")}
            value={ot.freightForwarder ?? ""}
            onSave={(v) => patch.mutate({ freightForwarder: v })}
            tooltip={t("created.freightForwarderHelp")}
            required
            disabled={locked}
          />
          <Field
            label={t("created.bookingRef")}
            value={ot.bookingRef ?? ""}
            onSave={(v) => patch.mutate({ bookingRef: v })}
            tooltip={t("created.bookingRefHelp")}
            disabled={locked}
          />
          <Field
            label={t("created.vessel")}
            value={ot.vessel ?? ""}
            onSave={(v) => patch.mutate({ vessel: v })}
            required
            disabled={locked}
          />
          <Field
            label={t("created.voyage")}
            value={ot.voyage ?? ""}
            onSave={(v) => patch.mutate({ voyage: v })}
            required
            disabled={locked}
          />
          <DateField
            label={t("created.etd")}
            value={ot.etd ?? null}
            onSave={(v) => patch.mutate({ etd: v })}
            tooltip={t("created.etdHelp")}
            required
            disabled={locked}
          />
          <DateField
            label={t("created.eta")}
            value={ot.eta ?? null}
            onSave={(v) => patch.mutate({ eta: v })}
            tooltip={t("created.etaHelp")}
            disabled={locked}
          />
        </div>

      </Card>

      <StageDocGrid ot={ot} stage="CREATED" />

      <LotsCard ot={ot} />
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  onSave: (value: string) => void
  type?: string
  required?: boolean
  placeholder?: string
  tooltip?: string
  disabled?: boolean
}

function Field(props: FieldProps) {
  // Re-mount when the server value changes so we don't have to mirror
  // it via useEffect — keeps the lint rule happy and avoids cascading renders.
  return <FieldInner key={props.value} {...props} />
}

function FieldInner({
  label,
  value,
  onSave,
  type = "text",
  required,
  placeholder,
  tooltip,
  disabled,
}: FieldProps) {
  const [draft, setDraft] = useState(value)
  const dirty = draft !== value

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel label={label} required={required} tooltip={tooltip} />
      <Input
        value={draft}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={disabled}
        className={disabled ? "disabled:opacity-100" : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (!disabled && dirty) onSave(draft)
        }}
      />
    </div>
  )
}

interface DateFieldProps {
  label: string
  value: string | null
  onSave: (value: string | null) => void
  required?: boolean
  tooltip?: string
  disabled?: boolean
}

function DateField({
  label,
  value,
  onSave,
  required,
  tooltip,
  disabled,
}: DateFieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel label={label} required={required} tooltip={tooltip} />
      <DatePopover
        value={value}
        disabled={disabled}
        onChange={(iso) => {
          if (iso !== (value ?? "")) onSave(iso)
        }}
      />
    </div>
  )
}

function FieldLabel({
  label,
  required,
  tooltip,
}: {
  label: string
  required?: boolean
  tooltip?: string
}) {
  const labelEl = (
    <Label className="flex items-center gap-1">
      {label}
      {required ? <span className="text-red-500">*</span> : null}
    </Label>
  )
  if (!tooltip) return labelEl
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">{labelEl}</span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}


function LotsCard({ ot }: { ot: OT }) {
  const { t } = useTranslation("transit")
  const navigate = useNavigate()
  const links = useMemo(() => ot.lots ?? [], [ot.lots])

  const totalMT = useMemo(
    () => links.reduce((acc, l) => acc + (l.lot?.netKg ?? 0) / 1000, 0),
    [links],
  )

  return (
    <Card className="flex flex-col gap-3 p-5">
      <SectionTitle
        eyebrow={t("created.lotsEyebrow")}
        title={t("created.lotsTitle", { count: links.length })}
      />

      {links.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-6 text-center text-sm">
          {t("created.lotsEmpty")}
        </p>
      ) : (
        <ul className="divide-border/60 divide-y rounded-md border">
          {links.map((link) => (
            <li
              key={link.id}
              className="flex items-center gap-3 px-3 py-2.5"
            >
              <button
                type="button"
                className="hover:underline font-mono text-sm font-medium"
                onClick={() => navigate(paths.lotDetail(link.lotId))}
              >
                {link.lot?.lotNumber ?? `#${link.lotId}`}
              </button>
              <span className="text-muted-foreground text-xs tabular-nums">
                {link.lot?.bags ?? "—"} sacs ·{" "}
                {((link.lot?.netKg ?? 0) / 1000).toFixed(3)} MT
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="text-muted-foreground text-xs tabular-nums">
        {t("created.lotsTotal", { mt: totalMT.toFixed(3) })}
      </div>
    </Card>
  )
}
